import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { JOB_POSTINGS, formatPostedDateMontreal } from './jobsData';

export default function CareersHomePage() {
  const { t, i18n } = useTranslation();

  const scrollToRoles = () => {
    document.getElementById('open-roles')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Header />
      <section
        className="relative min-h-[85vh] flex flex-col justify-end md:justify-center pt-28 pb-16 md:pb-24 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(115deg, rgba(28, 25, 23, 0.92) 0%, rgba(62, 47, 35, 0.88) 45%, rgba(120, 90, 65, 0.75) 100%), url('https://readdy.ai/api/search-image?query=Modern%20fintech%20office%20Montreal%20skyline%20team%20collaboration%20digital%20banking%20screens%20warm%20professional%20lighting%20wide%20interior&width=1920&height=1080&seq=careers-hero&orientation=landscape')`,
        }}
      >
        <div className="container mx-auto px-6 w-full relative z-10">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#d4b896] mb-4">
              {t('careers.badge')}
            </p>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-[1.1]">
              {t('careers.heroTitle')}
            </h1>
            <p className="text-lg md:text-xl text-stone-200 max-w-2xl leading-relaxed mb-10">
              {t('careers.heroSubtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                type="button"
                onClick={scrollToRoles}
                className="inline-flex items-center justify-center rounded-lg bg-[#9F7B5B] px-8 py-3.5 text-white font-semibold shadow-lg shadow-black/20 transition hover:bg-[#8a6a4f] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-stone-900"
              >
                {t('careers.heroCta')}
                <i className="ri-arrow-down-line ml-2 text-xl" aria-hidden />
              </button>
              <a
                href="mailto:careers@flexygroup.ca"
                className="inline-flex items-center justify-center rounded-lg border-2 border-white/40 bg-white/5 px-8 py-3.5 text-white font-semibold backdrop-blur-sm transition hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                {t('careers.heroSecondaryCta')}
              </a>
            </div>
          </div>
        </div>
      </section>

      <main id="open-roles" className="scroll-mt-24 py-16 md:py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">{t('careers.openRolesTitle')}</h2>
            <p className="text-gray-600 text-lg">{t('careers.openRolesSubtitle')}</p>
          </div>

          <div className="grid gap-8 md:grid-cols-2 xl:grid-cols-2 max-w-6xl">
            {JOB_POSTINGS.map((job) => {
              const posted = formatPostedDateMontreal(
                i18n.language,
                job.posted.year,
                job.posted.month,
                job.posted.day
              );
              return (
                <Link
                  key={job.slug}
                  to={`/careers/${job.slug}`}
                  className="group flex flex-col rounded-2xl border border-stone-200 bg-white p-8 shadow-sm transition duration-300 hover:border-[#9F7B5B]/40 hover:shadow-xl hover:shadow-stone-200/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9F7B5B] focus-visible:ring-offset-2"
                >
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-800 ring-1 ring-emerald-200/80">
                      <span className="mr-1 h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden />
                      {t('careers.statusActive')}
                    </span>
                    <span className="text-xs text-gray-500">{t('careers.postedOn', { date: posted })}</span>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-4 leading-snug group-hover:text-[#9F7B5B] transition-colors">
                    {job.title}
                  </h3>

                  <div className="flex flex-wrap gap-2 mb-4 text-sm text-gray-600">
                    <span className="inline-flex items-center rounded-lg bg-stone-100 px-3 py-1">
                      <i className="ri-map-pin-line mr-1.5 text-[#9F7B5B]" aria-hidden />
                      {job.location}
                    </span>
                    <span className="inline-flex items-center rounded-lg bg-stone-100 px-3 py-1">
                      <i className="ri-briefcase-line mr-1.5 text-[#9F7B5B]" aria-hidden />
                      {job.employmentType}
                    </span>
                    <span className="inline-flex items-center rounded-lg bg-stone-100 px-3 py-1">
                      <i className="ri-building-line mr-1.5 text-[#9F7B5B]" aria-hidden />
                      {job.workArrangement}
                    </span>
                    <span className="inline-flex items-center rounded-lg bg-stone-100 px-3 py-1">
                      <i className="ri-money-dollar-circle-line mr-1.5 text-[#9F7B5B]" aria-hidden />
                      {job.salary}
                    </span>
                  </div>

                  <p className="text-gray-600 leading-relaxed line-clamp-4 flex-1 mb-6">{t(job.excerptKey)}</p>

                  <span className="inline-flex items-center text-sm font-semibold text-[#9F7B5B] mt-auto">
                    {t('careers.viewRole')}
                    <i className="ri-arrow-right-line ml-1 transition-transform group-hover:translate-x-1" aria-hidden />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
